import type { ReactNode } from 'react';
import { Empty, type EmptyProps } from './Empty.js';
import { Loading, type LoadingProps } from './Loading.js';
import { ErrorFallback, type ErrorFallbackProps } from './ErrorFallback.js';

export interface DataViewProps<T> {
  /** Whether the data request is in flight. Takes precedence over `error`
   *  on the initial fetch; a fresh in-flight request after success renders
   *  the stale children, not the loading state. */
  loading?: boolean;
  /** Error from the most recent attempt, if any. Wins over `empty` so we
   *  never mislead users into thinking "there's no data". */
  error?: Error | undefined;
  /** The data payload. Used to decide `empty` by calling `isEmpty`. */
  data?: T | undefined;
  /** Custom empty predicate. Defaults to:
   *    - arrays: `length === 0`
   *    - anything else: nullish.  */
  isEmpty?: (data: T | undefined) => boolean;
  /** Rendered when `loading` is true. */
  loadingFallback?: ReactNode;
  /** Rendered when `error` is set. Receives the error + a retry callback. */
  errorFallback?: ReactNode | ((error: Error, retry: (() => void) | undefined) => ReactNode);
  /** Rendered when data is present but empty. */
  emptyFallback?: ReactNode;
  /** Called by the default error fallback's Retry button. Optional. */
  onRetry?: () => void;
  /** Success renderer. Receives the resolved data. */
  children: (data: T) => ReactNode;
  /** Passthrough props for the default Empty component. Ignored when
   *  `emptyFallback` is provided. */
  emptyProps?: Omit<EmptyProps, 'className'>;
  /** Passthrough props for the default Loading component. */
  loadingProps?: Omit<LoadingProps, 'className'>;
  /** Passthrough props for the default ErrorFallback component. */
  errorProps?: Omit<ErrorFallbackProps, 'error' | 'onRetry' | 'className'>;
}

function defaultIsEmpty(data: unknown): boolean {
  if (data === undefined || data === null) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

/** Three-state combinator — enforces the `error → loading → empty → success`
 *  render order spec 14 §2.2 mandates. Consumers pass `{ loading, error,
 *  data }` and a success renderer. Defaults cover 90% of call sites; any
 *  fallback can be overridden when the context demands something richer. */
export function DataView<T>(props: DataViewProps<T>): ReactNode {
  const {
    loading = false,
    error,
    data,
    isEmpty = defaultIsEmpty,
    loadingFallback,
    errorFallback,
    emptyFallback,
    onRetry,
    children,
    emptyProps,
    loadingProps,
    errorProps,
  } = props;

  if (error !== undefined) {
    if (errorFallback !== undefined) {
      return typeof errorFallback === 'function' ? errorFallback(error, onRetry) : errorFallback;
    }
    const errorBoxProps: ErrorFallbackProps = { ...errorProps, error };
    if (onRetry !== undefined) errorBoxProps.onRetry = onRetry;
    return <ErrorFallback {...errorBoxProps} />;
  }
  if (loading) {
    return loadingFallback ?? <Loading {...loadingProps} />;
  }
  if (isEmpty(data)) {
    return emptyFallback ?? <Empty {...emptyProps} />;
  }
  return children(data as T);
}
